import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { DataListComponent, DataListSortOption } from '../../../shared/ui/data-list/data-list.component';
import { DataFetch } from '../../../models/data-source';
import { AuthService } from '../../../services/auth.service';
import { WorkspaceNavigationService } from '../../../services/workspace-navigation.service';
import { OverviewAnalysisListItem, OverviewApiService, OverviewTemplate } from '../overview-api.service';
import { reportPeriodLabel } from '../overview-model';

/** Report templates: system templates plus this company's own; each expands to the reports it generated. */
@Component({
  selector: 'app-report-templates',
  templateUrl: './report-templates.component.html',
  host: { class: 'workspace-page--full' },
})
export class ReportTemplatesComponent implements OnDestroy {
  @ViewChild('table') table?: DataTableComponent<OverviewTemplate>;
  @ViewChild('list') list?: DataListComponent<OverviewTemplate>;
  search = '';
  readonly columns: DataTableColumn[] = [
    { id: 'name', label: 'Template' },
    { id: 'description', label: 'Description', sortable: false },
    { id: 'source', label: 'Source' },
    { id: 'status', label: 'Status' },
    { id: 'reportCount', label: 'Reports', align: 'end' },
    { id: 'createdAt', label: 'Created' },
    { id: 'updatedAt', label: 'Updated' },
  ];
  readonly sortOptions: readonly DataListSortOption[] = [
    { label: 'Name', field: 'name' }, { label: 'Created', field: 'createdAt', desc: true }, { label: 'Updated', field: 'updatedAt', desc: true },
    { label: 'Reports', field: 'reportCount', desc: true },
  ];
  readonly fetch: DataFetch<OverviewTemplate> = query => this.api.templates({
    offset: (query.page - 1) * query.pageSize, pageSize: query.pageSize,
    search: query.search, sort: query.sort, desc: query.desc, includeInactive: true,
  });
  readonly trackById = (_: number, item: { id: string }) => item.id;
  readonly reportPeriod = reportPeriodLabel;
  private readonly reportFetches = new Map<string, DataFetch<OverviewAnalysisListItem>>();
  private readonly session: Subscription;
  private sessionReload?: ReturnType<typeof setTimeout>;
  constructor(private readonly api: OverviewApiService, public readonly auth: AuthService,
    private readonly navigation: WorkspaceNavigationService) {
    // The shared table and list clear themselves for the new company; once they have, load its templates.
    this.session = auth.tenant.sessionChanged$.subscribe(() => {
      this.reportFetches.clear(); clearTimeout(this.sessionReload);
      this.sessionReload = setTimeout(() => this.refresh());
    });
  }
  ngOnDestroy(): void { this.session.unsubscribe(); clearTimeout(this.sessionReload); }
  /** A report opened from here returns here, not to Overview. */
  returnState(): Record<string, unknown> { return this.navigation.sourceState('Report templates'); }
  refresh(): void { this.table?.refresh(); this.list?.refresh(); }
  /** One stable fetch per template, so an expanded template keeps its loaded reports across change detection. */
  reports(templateId: string): DataFetch<OverviewAnalysisListItem> {
    let fetch = this.reportFetches.get(templateId);
    if (!fetch) {
      fetch = query => this.api.analysisList({ templateId, offset: (query.page - 1) * query.pageSize, pageSize: query.pageSize });
      this.reportFetches.set(templateId, fetch);
    }
    return fetch;
  }
}
